<template>
  <v-sheet class="privacy">
    <v-row>
      <v-col>
        <h1 class="mt-10 mb-15 text-center">
          CALIFORNIA CONSUMER PRIVACY ACT (CCPA)
        </h1>

        <p>
          Hire Next Generation LLC is complying with the CCPA (as it pertains to
          us) and considering its underlying principles in how we think about
          customer trust and data privacy as a core pillar of our business. We
          are providing this CCPA-specific privacy notice to supplement the
          information and disclosures already contained in our Privacy Policy.
          This notice applies to individuals residing in California from whom we
          collect Personal Information.
        </p>

        <p>In general, we do not sell your Personal Information.</p>

        <p>
          The chart below outlines the categories of Personal Information (as
          defined by the CCPA) that we have collected and/or disclosed for a
          business purpose in the preceding twelve months. The examples of
          Personal Information provided for each category are taken from the
          CCPA and are included to help you understand what the categories mean.
          The examples are not meant to indicate what we actually collect or
          disclose, and more information about our specific practices can be
          found in our Privacy Policy.
        </p>

        <v-simple-table class="mb-10 ccpa-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Category</th>
                <th class="text-left">We Collect</th>
                <th class="text-left">We Disclose</th>
                <th class="text-left">We Sell</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A. Identifiers</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Name, alias, postal address, unique personal
                  identifier, online identifier, internet protocol address,
                  email address, account name, social security number, driver’s
                  license number, passport number, or other similar identifiers.
                </td>
              </tr>
              <tr>
                <td>
                  B. Categories of Personal Information in Cal. Civ. Code
                  1798.80(e)
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Name, signature, social security number, physical
                  characteristics or description, address, telephone number,
                  passport number, driver’s license or state identification card
                  number, insurance policy number, education, employment,
                  employment history, bank account number, credit card number,
                  debit card number, or any other financial information, medical
                  information, or health insurance information.
                </td>
              </tr>
              <tr>
                <td>
                  C. Characteristics of Protected Classifications under
                  California or Federal Law
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Race or color, ancestry or national origin, religion
                  or creed, age (over 40), mental or physical disability, sex
                  (including gender and pregnancy, childbirth, breastfeeding or
                  related medical conditions), sexual orientation, gender
                  identity or expression, medical condition, genetic
                  information, marital status, military and veteran status.
                </td>
              </tr>

              <tr>
                <td>D. Commercial Information</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Records of personal property, products or services
                  purchased, obtained, reviewed, or considered, or other
                  purchasing or consuming histories or tendencies.
                </td>
              </tr>
              <tr>
                <td>E. Biometric Information</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Physiological, biological, or behavioral
                  characteristics, including DNA, that can be used, singly or in
                  combination with each other or with other identifying data, to
                  establish individual identity, such as imagery of the iris,
                  retina, fingerprint, face, hand, palm, vein patterns, and
                  voice recordings, from which an identifier template, such as a
                  faceprint, a minutiae template, or a voiceprint, can be
                  extracted, and keystroke patterns or rhythms, gait patterns or
                  rhythms, and sleep, health, or exercise data that contain
                  identifying information.
                </td>
              </tr>
              <tr>
                <td>
                  F. Internet or Other Electronic Network Activity Information
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Browsing history, search history, and information
                  regarding a consumer’s interaction with an internet website,
                  application or advertisement.
                </td>
              </tr>
              <tr>
                <td>G. Geolocation Data</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>Example: Precise physical location.</td>
              </tr>
              <tr>
                <td>H. Sensory Information</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Audio, electronic, visual, thermal, olfactory, or
                  similar information.
                </td>
              </tr>
              <tr>
                <td>I. Professional or employment-related information</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Job application or resume information, past and
                  current job history, and job performance information.
                </td>
              </tr>
              <tr>
                <td>
                  J. Non-Public Education Information (as defined in 20 U.S.C.
                  1232g; 34 C.F.R. Part 99)
                </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Records that are directly related to a student
                  maintained by an educational agency or institution or by a
                  party acting for the agency or institution.
                </td>
              </tr>

              <tr>
                <td>K. Inferences Drawn from Personal Information</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>
                  Examples: Consumer profiles reflecting a consumer’s
                  preferences, characteristics, psychological trends,
                  preferences, predispositions, behavior, attitudes,
                  intelligence, abilities, and aptitudes.
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <h3>Use of Personal Information</h3>

        <p>
          The CCPA defines various business and commercial purposes for
          collecting, using, and disclosing Personal Information. While we
          collect, use, and disclose Personal Information pursuant to our
          Privacy Policy as a whole, we wanted to clarify that this includes
          Personal Information in accordance with the specific CCPA business and
          commercial purposes as follows:
        </p>

        <ol class="mb-5">
          <li>
            Auditing related to a current interaction with you and concurrent
            transactions, including, but not limited to auditing compliance with
            this specification and other standards.
          </li>
          <li>
            Detecting security incidents, protecting against malicious,
            deceptive, fraudulent, or illegal activity, and prosecuting those
            responsible for that activity.
          </li>
          <li>
            Debugging to identify and repair errors that impair existing
            intended functionality.
          </li>
          <li>Short-term, transient use.</li>
          <li>
            Contracting with service providers to perform services on our
            behalf, including maintaining or servicing accounts, providing
            customer service, processing or fulfilling orders and transactions,
            verifying customer information, processing payments, providing
            advertising or marketing services, providing analytic services, or
            providing similar services on behalf of the business or service
            provider.
          </li>
          <li>
            Undertaking internal research for technological development and
            demonstration.
          </li>
          <li>
            Undertaking activities to verify or maintain the quality or safety
            of our services, and to improve, upgrade, or enhance our services.
          </li>
          <li>
            Otherwise enabling or effecting, directly or indirectly, a
            commercial transaction.
          </li>
          <li>
            For other purposes for which we provide specific notice at the time
            the information is collected.
          </li>
        </ol>

        <h3>Collection and Disclosure of Personal Information</h3>

        <p>
          In the preceding twelve months since this notice was last updated, we
          have collected Personal Information from general sources including
          you, your use of our services, your devices, our affiliates, our
          vendors, and our service providers. More specific information about
          Personal Information we collect is laid out in this notice and in our
          Privacy Policy, which we update from time to time.
        </p>

        <p>
          As also explained in our Privacy Policy, we share your Personal
          Information with the following categories of CCPA third parties:
        </p>

        <ol class="mb-5">
          <li>Affiliates.</li>
          <li>
            Vendors and service providers, including for data analytics and
            marketing and advertising our products and services to you.
          </li>
          <li>Third parties integrated into our services.</li>
          <li>Third parties as required by law and similar disclosures.</li>
          <li>
            Third parties in connection with a merger, sale, or asset transfer.
          </li>
          <li>
            Other third parties for whom we have obtained your permission to
            disclose your Personal Information.
          </li>
        </ol>

        <h3>Your California Privacy Rights</h3>
        <p>
          If you are a California resident, you may exercise the following
          rights.
        </p>

        <p>
          Right to Know and Access. You may submit a verifiable request for
          information regarding the: (1) categories of Personal Information
          collected or disclosed by us; (2) purposes for which categories of
          Personal Information are collected by us; (3) categories of sources
          from which we collect Personal Information; and (4) specific pieces of
          Personal Information we have collected about you during the past
          twelve months.
        </p>

        <p>
          Right to Delete. Subject to certain exceptions, you have the option to
          delete Personal Information about you that we have collected from you.
        </p>

        <p>
          Verification. Requests for access to or deletion of Personal
          Information are subject to our ability to reasonably verify your
          identity in light of the information requested and pursuant to
          relevant CCPA requirements, limitations, and regulations.
        </p>

        <p>
          Right to Equal Service and Price. You have the right not to receive
          discriminatory treatment for the exercise of your CCPA privacy rights,
          subject to certain limitations.
        </p>

        <p>
          Shine the Light. We do not rent, sell, or share your Personal
          Information with nonaffiliated companies for their direct marketing
          purposes, unless we have your permission.
        </p>

        <p>Contact Information</p>

        <p>
          If you have any questions, or would like more information on our
          privacy practices, please contact us at hireme@hirenextgeneration.com
          or by mail at:
        </p>

        <address>
          Hire Next Generation LLC 201<br />
          E. Pine Street, Suite 205<br />
          Orlando, Florida 32801
        </address>
      </v-col>
    </v-row>
  </v-sheet>
</template>
<script>
export default {};
</script>

<style lang="scss">
.ccpa-table {
  th {
    background: #0274b3;
    &:first-child {
      width: 70%;
    }
  }
  tbody {
    tr:nth-child(odd) {
      background: #ccc;
      td:first-child {
        font-weight: bold;
      }
    }
  }
}
</style>
